import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { exchangeSsoToken, getMe, verifyOtp } from '../api/client';
import type { AuthUser, UserProfile } from '../api/types';
import {
  captureEmbedFlag,
  setSsoError,
  stripSsoParamsFromUrl,
} from '../utils/embed';

interface AuthContextValue {
  user: UserProfile | null;
  token: string | null;
  loading: boolean;
  login: (email: string, code: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function applySessionToken(token: string): void {
  localStorage.setItem('ots_token', token);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('ots_token'));
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    const profile = await getMe();
    setUser(profile);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function boot() {
      captureEmbedFlag(window.location.search);
      const sso = new URLSearchParams(window.location.search).get('sso');

      if (sso) {
        try {
          const result = await exchangeSsoToken(sso);
          if (cancelled) return;
          applySessionToken(result.token);
          setToken(result.token);
          stripSsoParamsFromUrl();
          await refreshUser();
        } catch {
          if (cancelled) return;
          stripSsoParamsFromUrl();
          setSsoError();
          localStorage.removeItem('ots_token');
          setToken(null);
          setUser(null);
        } finally {
          if (!cancelled) setLoading(false);
        }
        return;
      }

      const stored = localStorage.getItem('ots_token');
      if (!stored) {
        setToken(null);
        setLoading(false);
        return;
      }
      setToken(stored);

      try {
        await refreshUser();
      } catch {
        localStorage.removeItem('ots_token');
        setToken(null);
        setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void boot();
    return () => {
      cancelled = true;
    };
  }, [refreshUser]);

  const login = useCallback(async (email: string, code: string) => {
    const { token: next } = await verifyOtp(email, code);
    applySessionToken(next);
    setToken(next);
    await refreshUser();
  }, [refreshUser]);

  const logout = useCallback(() => {
    localStorage.removeItem('ots_token');
    setToken(null);
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, token, loading, login, logout, refreshUser }),
    [user, token, loading, login, logout, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function useAuthUser(): AuthUser {
  const { user } = useAuth();
  if (!user) throw new Error('Not authenticated');
  return user;
}
